/* eslint-disable react-refresh/only-export-components -- isIsoDate is exported alongside the
   component for CalendarDateInput.test.tsx's own dedicated date-validity cases; it's a pure
   function, not a second component, so fast-refresh granularity doesn't apply. */
// Ported from ptc-supervision src/components/dates/CalendarDateInput.tsx (PR #240, branch
// cv/sup-calendar-picker, not yet merged there -- fleet filter contract v2.3 §12.6, RULE D8 as
// amended 2026-10-27, C68). Changes from the source: `i18n`/`useTranslation` both come from
// react-i18next's hook (`const { t, i18n } = useTranslation()`) rather than a custom re-export
// module -- this app's i18n/index.ts only has a default export, no named `useTranslation`.
//
// THE date control (fleet filter contract v2.3 §12.6, RULE D8 as amended 2026-10-27).
//
// Why not a native `<input type="date">` any more: Chromium's own calendar WRITES the field's
// value when you move between months. Where a value change commits, the page then reloads around
// the open calendar and the day can never be chosen. Nothing in the page can stop the browser
// doing that, so the control has to be one we own. The rule it keeps:
//
//   - the month arrows move the VIEW only. The value is untouched (C68);
//   - only clicking a day (or typing a complete date) sets the value;
//   - committing that value is the caller's job (an Apply button, a dialog's Save). This control
//     never commits.
//
// It renders INLINE, inside its own wrapper, never in a portal, so an enclosing popover's
// outside-click check still sees a click on a day as inside, and Escape closes only the calendar
// (stopped here), not the popover around it. The value is always `yyyy-MM-dd`, whatever language
// is displayed.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CalendarRegular, ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';

export interface CalendarDateInputProps {
  /** `yyyy-MM-dd`, or '' for no date. */
  value: string;
  /** Called with a complete `yyyy-MM-dd` (a day clicked, or a full date typed), or '' when cleared. Never on navigation. */
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Only when no wrapping `<Field>` names the input. */
  'aria-label'?: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date in `yyyy-MM-dd` (rejects 2026-02-30). */
export function isIsoDate(text: string): boolean {
  const m = ISO_DATE.exec(text);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

interface MonthView { year: number; month: number } // month 0-11

function iso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function viewOf(value: string, fallback: string): MonthView {
  const src = isIsoDate(value) ? value : fallback;
  return { year: +src.slice(0, 4), month: +src.slice(5, 7) - 1 };
}

function shift(v: MonthView, months: number): MonthView {
  const n = v.year * 12 + v.month + months;
  return { year: Math.floor(n / 12), month: ((n % 12) + 12) % 12 };
}

/** Today as `yyyy-MM-dd` in the reader's own calendar. */
function localToday(): string {
  const d = new Date();
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
}

const useStyles = makeStyles({
  root: { position: 'relative', display: 'inline-flex', flexDirection: 'column' },
  input: { minWidth: '150px' },
  popup: {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 1000,
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    width: '252px',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalXS },
  title: { fontWeight: tokens.fontWeightSemibold, textTransform: 'capitalize' },
  grid: { width: '100%', borderCollapse: 'collapse' },
  weekday: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular, padding: '2px 0' },
  cell: { padding: '1px', textAlign: 'center' },
  day: { minWidth: '32px', width: '32px', height: '32px', padding: 0 },
  today: { border: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}` },
});

export function CalendarDateInput({ value, onChange, min, max, size = 'medium', disabled, 'aria-label': ariaLabel }: CalendarDateInputProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MonthView>(() => viewOf(value, localToday()));
  // The day that holds keyboard focus inside the grid (roving tabindex).
  const [focusDay, setFocusDay] = useState<string | null>(null);

  // A value set from outside (a day picked, a reset) replaces whatever was typed.
  useEffect(() => { setText(value); }, [value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openCalendar = () => {
    setView(viewOf(value, localToday()));
    setFocusDay(null);
    setOpen(true);
  };

  const pick = (day: string) => {
    setText(day);
    setOpen(false);
    if (day !== value) onChange(day);
  };

  const inRange = (day: string) => (!min || day >= min) && (!max || day <= max);

  return (
    <div ref={rootRef} className={styles.root}>
      <Input
        className={styles.input}
        size={size}
        disabled={disabled}
        aria-label={ariaLabel}
        value={text}
        placeholder={t('datePicker.placeholder')}
        onChange={(_, d) => {
          setText(d.value);
          if (d.value === '') onChange('');
          else if (isIsoDate(d.value) && d.value !== value) onChange(d.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); openCalendar(); }
        }}
        // Typed text that never became a complete, real date is reverted on blur -- the field
        // never shows a date the form doesn't actually hold. Conditioned (not a bare
        // setText(value)): a surface that stages and doesn't echo the value back synchronously
        // would otherwise have a complete typed date overwritten by this handler too. Matches
        // ptc-supervision's reference at 04a2326 (not the earlier c4dbfe9 I first copied, which
        // had no onBlur at all -- caught by this exact test, then confirmed against the correct
        // source per ptc-supervision-1f).
        onBlur={() => { if (text !== value && text !== '' && !isIsoDate(text)) setText(value); }}
        contentAfter={
          <Button
            appearance="transparent"
            size="small"
            icon={<CalendarRegular />}
            disabled={disabled}
            aria-label={t('datePicker.open')}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => (open ? setOpen(false) : openCalendar())}
          />
        }
      />
      {open && (
        <MonthGrid
          view={view}
          onView={setView}
          value={value}
          focusDay={focusDay}
          onFocusDay={setFocusDay}
          onPick={pick}
          inRange={inRange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function MonthGrid({
  view, onView, value, focusDay, onFocusDay, onPick, inRange, onClose,
}: {
  view: MonthView;
  onView: (v: MonthView) => void;
  value: string;
  focusDay: string | null;
  onFocusDay: (day: string) => void;
  onPick: (day: string) => void;
  inRange: (day: string) => boolean;
  onClose: () => void;
}) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const gridRef = useRef<HTMLTableElement>(null);
  const today = localToday();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es') ? 'es' : 'en-US';

  const { title, weekdays, weeks, dayLabel } = useMemo(() => {
    const monthFmt = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const wdFmt = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
    const fullFmt = new Intl.DateTimeFormat(lang, { dateStyle: 'full', timeZone: 'UTC' });
    // 2026-10-04 is a Sunday: the week starts on Sunday, as the browser calendar it replaces did.
    const names = Array.from({ length: 7 }, (_, i) => wdFmt.format(new Date(Date.UTC(2026, 9, 4 + i))));
    const first = new Date(Date.UTC(view.year, view.month, 1));
    const daysIn = new Date(Date.UTC(view.year, view.month + 1, 0)).getUTCDate();
    const cells: (string | null)[] = Array.from({ length: first.getUTCDay() }, () => null);
    for (let d = 1; d <= daysIn; d++) cells.push(iso(view.year, view.month, d));
    while (cells.length % 7) cells.push(null);
    const rows: (string | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return {
      title: monthFmt.format(first),
      weekdays: names,
      weeks: rows,
      dayLabel: (day: string) => fullFmt.format(new Date(`${day}T00:00:00Z`)),
    };
  }, [view, lang]);

  const monthDays = weeks.flat().filter((d): d is string => d !== null);
  const tabbable =
    (focusDay && monthDays.includes(focusDay) && focusDay) ||
    (monthDays.includes(value) && value) ||
    (monthDays.includes(today) && today) ||
    monthDays[0];

  // After a keyboard move (possibly into another month) put focus on the new day.
  useEffect(() => {
    if (!focusDay) return;
    gridRef.current?.querySelector<HTMLButtonElement>(`button[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, view]);

  const moveFocus = (from: string, days: number) => {
    const d = new Date(`${from}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    const next = iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    if (next.slice(0, 7) !== from.slice(0, 7)) onView({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    onFocusDay(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Close the calendar only: an enclosing filter popover must not read this Escape as "discard".
      e.stopPropagation();
      e.preventDefault();
      onClose();
      return;
    }
    const day = (e.target as HTMLElement).dataset?.day;
    if (!day) return;
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (step !== undefined) { e.preventDefault(); moveFocus(day, step); }
  };

  return (
    <div role="dialog" aria-label={t('datePicker.calendar')} className={styles.popup} onKeyDown={onKeyDown}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronLeftRegular />}
          aria-label={t('datePicker.previousMonth')}
          onClick={() => onView(shift(view, -1))}
        />
        <span className={styles.title} aria-live="polite">{title}</span>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronRightRegular />}
          aria-label={t('datePicker.nextMonth')}
          onClick={() => onView(shift(view, 1))}
        />
      </div>
      <table ref={gridRef} role="grid" aria-label={title} className={styles.grid}>
        <thead>
          <tr>
            {weekdays.map((w) => (
              <th key={w} scope="col" className={styles.weekday}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, i) => (
            <tr key={i}>
              {week.map((day, j) => (
                <td key={j} className={styles.cell} role={day ? 'gridcell' : undefined}>
                  {day && (
                    <Button
                      data-day={day}
                      className={mergeClasses(styles.day, day === today && styles.today)}
                      size="small"
                      appearance={day === value ? 'primary' : 'subtle'}
                      aria-label={dayLabel(day)}
                      aria-pressed={day === value}
                      aria-current={day === today ? 'date' : undefined}
                      tabIndex={day === tabbable ? 0 : -1}
                      disabled={!inRange(day)}
                      onClick={() => onPick(day)}
                    >
                      {Number(day.slice(8))}
                    </Button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
